import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

// Vehicle Parts props
interface VehiclePropsData {
  vehicleTypeData: string | null;
  partsData?: string | null;
  makeData?: string | null;
  countType?: string | null;
}

// Get Part
type Part = {
  name: string;
  count: number;
  items: string[];
};

// Get vehicle config state props
interface GetVehicleConfigData {
  message: string;
  data: Part[] | [];
}

// Type for make types
type MakeType = {
  typeName: string;
  typeId: number;
  isDeleted?: number;
};

// Type for make list
type PartMake = {
  make: string;
  makeId: number;
  makeTypes: MakeType[];
  isDeleted?: number;
};

// Type for vehicle parts list
type VehiclePart = {
  part: string;
  partId: number;
  partMakeList: PartMake[];
  isDelete?: number;
};

// All parts list
interface ListOfUpdateData {
  vehicleType: string;
  vehiclePartList: VehiclePart[];
}

// Get all vehicle config data state props
interface GetResForUpdate {
  message: string;
  data: ListOfUpdateData | null;
}

// Initial State Props
interface InitialStateProps {
  isLoading: {
    isAllLoading: boolean;
    isPartsLoading: boolean;
    isMakeLoading: boolean;
    isTypeLoading: boolean;
    isNAConfigLoading: boolean;
    isAddLoading: boolean;
    isUpdateLoading: boolean;
    isDeleteLoading: boolean;
  };
  data: {
    getAllList: GetResForUpdate | null;
    getPartsList: GetVehicleConfigData | null;
    getMakeList: GetVehicleConfigData | null;
    getTypeList: GetVehicleConfigData | null;
    getNAConfig: GetVehicleConfigData | null;
    addVehicleConfig: AddResponseData | null;
    updateVehicleConfig: AddResponseData | null;
    deleteVehicleConfig: DeleteRes | null;
  };
  error: string | null;
}

// Define the initial state
const initialState: InitialStateProps = {
  isLoading: {
    isAllLoading: false,
    isPartsLoading: false,
    isMakeLoading: false,
    isTypeLoading: false,
    isNAConfigLoading: false,
    isAddLoading: false,
    isUpdateLoading: false,
    isDeleteLoading: false
  },
  data: {
    getAllList: null,
    getPartsList: null,
    getMakeList: null,
    getTypeList: null,
    getNAConfig: null,
    addVehicleConfig: null,
    updateVehicleConfig: null,
    deleteVehicleConfig: null
  },
  error: null
};

// Add cofig data payload type
interface AddVehicleConfigManager {
  vehicleType: string | null;
  vehiclePartList: PartData[];
}

interface PartData {
  part: string | null;
  partMakeList: ModelData[];
}

interface ModelData {
  make: string | null;
  modelTypes: string[];
}

// Add cofig data response type
interface AddResponseData {
  status: string;
  message: string;
  data: [];
}

// Delete Payload Props:
interface DeleteType {
  vehicleType: string | null;
  deleteVehiclePart?: string | null;
  deleteVehicleMake?: string | null;
  deleteMakeType?: string | null;
  selectedType?: string | null;
}

// Delete Response:
interface DeleteRes {
  status: string;
  message: string;
}

// GET all parts list details while update:
export const getAllVehiclePartsList = createAsyncThunk<GetResForUpdate, VehiclePropsData>(
  'get/all/vehicle/parts/list',
  async (dataProps, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_CONFIG_LIST}part?vehicleType=${dataProps?.vehicleTypeData}&vehiclePart=${dataProps?.partsData}`
      });
      return response.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// GET parts list details:
export const getVehiclePartsList = createAsyncThunk<
  GetVehicleConfigData,
  VehiclePropsData
>('get/vehicle/parts/list', async (dataProps, thunkApi) => {
  try {
    let URL = `${URLs.GET_CONFIG_LIST}`;

    if (dataProps?.countType === 'PART') {
      URL += `count?vehicleType=${dataProps?.vehicleTypeData}`;
    } else {
      URL += `data?vehicleType=${dataProps?.vehicleTypeData}`;
    }

    const response = await axiosInstance({
      method: 'GET',
      url: URL
    });

    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// GET make list details:
export const getVehicleMakeListData = createAsyncThunk<
  GetVehicleConfigData,
  VehiclePropsData
>('get/vehicle/make/list', async (dataProps, thunkApi) => {
  try {
    let URL = `${URLs.GET_CONFIG_LIST}`;

    if (dataProps?.countType === 'MAKE') {
      URL += `count?vehicleType=${dataProps?.vehicleTypeData}&part=${dataProps?.partsData}`;
    } else {
      URL += `data?vehicleType=${dataProps?.vehicleTypeData}&part=${dataProps?.partsData}`;
    }

    const response = await axiosInstance({
      method: 'GET',
      url: URL
    });
    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// GET type list details:
export const getVehicleMakeTypeList = createAsyncThunk<
  GetVehicleConfigData,
  VehiclePropsData
>('get/vehicleParts/type/list', async (dataProps, thunkApi) => {
  try {
    let URL = `${URLs.GET_CONFIG_LIST}`;

    if (dataProps?.countType === 'TYPE') {
      URL += `count?vehicleType=${dataProps?.vehicleTypeData}&part=${dataProps?.partsData}&make=${dataProps?.makeData}`;
    } else {
      URL += `data?vehicleType=${dataProps?.vehicleTypeData}&part=${dataProps?.partsData}&make=${dataProps?.makeData}`;
    }

    const response = await axiosInstance({
      method: 'GET',
      url: URL
    });
    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error);
  }
});

// Add vehicle cofig data:
export const addVehicleConfigList = createAsyncThunk<
  AddResponseData,
  AddVehicleConfigManager
>('add/vehicle/config/list', async (dataProps, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: `${URLs.ADD_CONFIG_LIST}`,
      data: dataProps
    });

    thunkApi.dispatch(
      updateToast({
        show: true,
        severity: 'success',
        message: response.data?.message
      })
    );

    return response.data;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error?.message,
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error);
  }
});

// Update vehicle cofig data:
export const updateVehicleConfigList = createAsyncThunk<
  AddResponseData,
  ListOfUpdateData
>('update/vehicle/config/list', async (dataProps, thunkApi) => {
  try {
    const response = await axiosInstance({
      method: 'PUT',
      url: `${URLs.UPDATE_CONFIG_LIST}`,
      data: dataProps
    });

    thunkApi.dispatch(
      updateToast({
        show: true,
        severity: 'success',
        message: response.data?.message
      })
    );

    return response.data;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error?.message,
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error);
  }
});

// Delete Part action file :
export const deleteVehicleConfigList = createAsyncThunk<DeleteRes, DeleteType>(
  'delete/vehicle/config/list',
  async (data, thunkApi) => {
    try {
      const {
        vehicleType,
        deleteVehiclePart,
        deleteVehicleMake,
        deleteMakeType,
        selectedType
      } = data;

      let URL = `${URLs.DELETE_CONFIG_LIST}vehicleType=${vehicleType}`;

      // Delete URL for deleting Parts, Make and Type based on it's Vehicle Type:
      if (selectedType === 'TYPE') {
        URL += `&part=${deleteVehiclePart}&make=${deleteVehicleMake}&type=${deleteMakeType}`;

        // Delete URL for deleting Parts and Make based on it's Vehicle Type:
      } else if (selectedType === 'MAKE') {
        URL += `&part=${deleteVehiclePart}&make=${deleteVehicleMake}`;

        // Delete URL for deleting Parts based on it's Vehicle Type:
      } else {
        URL += `&part=${deleteVehiclePart}`;
      }

      const response = await axiosInstance({
        method: 'DELETE',
        url: URL
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response.data?.message,
          severity: 'success'
        })
      );

      if (selectedType === 'TYPE') {
        await thunkApi.dispatch(
          getVehicleMakeTypeList({
            vehicleTypeData: vehicleType,
            partsData: deleteVehiclePart,
            makeData: deleteVehicleMake
          })
        );
      } else if (selectedType === 'MAKE') {
        clearTypeListData();
        await thunkApi.dispatch(
          getVehicleMakeListData({
            vehicleTypeData: vehicleType,
            partsData: deleteVehiclePart
          })
        );
      } else {
        clearTypeListData();
        clearMakeListData();
        await thunkApi.dispatch(
          getVehiclePartsList({
            vehicleTypeData: vehicleType
          })
        );
      }

      return response.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

// Slice file function for the vehicle config manager:
const vehicleConfigManagerFile = createSlice({
  name: 'Vehicle Config Manager Slice',
  initialState,
  reducers: {
    clearAllListData: (state: any) => {
      state.data.getAllList = [];
    },
    clearPartListData: (state: any) => {
      state.data.getPartsList = [];
    },
    clearMakeListData: (state: any) => {
      state.data.getMakeList = [];
    },
    clearTypeListData: (state: any) => {
      state.data.getTypeList = [];
    }
  },
  extraReducers: builder => {
    builder
      // GET All parts list:
      .addCase(getAllVehiclePartsList.pending, state => {
        state.isLoading.isAllLoading = true;
        state.data.getAllList = null;
        state.error = null;
      })

      .addCase(
        getAllVehiclePartsList.fulfilled,
        (state, action: PayloadAction<GetResForUpdate>) => {
          state.isLoading.isAllLoading = false;
          state.data.getAllList = action.payload;
          state.error = null;
        }
      )

      .addCase(getAllVehiclePartsList.rejected, (state, action: any) => {
        state.isLoading.isAllLoading = false;
        state.data.getAllList = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // GET parts list:
      .addCase(getVehiclePartsList.pending, state => {
        state.isLoading.isPartsLoading = true;
        state.data.getPartsList = null;
        state.error = null;
      })

      .addCase(
        getVehiclePartsList.fulfilled,
        (state, action: PayloadAction<GetVehicleConfigData>) => {
          state.isLoading.isPartsLoading = false;
          state.data.getPartsList = action.payload;
          state.error = null;
        }
      )

      .addCase(getVehiclePartsList.rejected, (state, action: any) => {
        state.isLoading.isPartsLoading = false;
        state.data.getPartsList = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // GET make list:
      .addCase(getVehicleMakeListData.pending, state => {
        state.isLoading.isMakeLoading = true;
        state.data.getMakeList = null;
        state.error = null;
      })

      .addCase(
        getVehicleMakeListData.fulfilled,
        (state, action: PayloadAction<GetVehicleConfigData>) => {
          state.isLoading.isMakeLoading = false;
          state.data.getMakeList = action.payload;
          state.error = null;
        }
      )

      .addCase(getVehicleMakeListData.rejected, (state, action: any) => {
        state.isLoading.isMakeLoading = false;
        state.data.getMakeList = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // GET type list:
      .addCase(getVehicleMakeTypeList.pending, state => {
        state.isLoading.isTypeLoading = true;
        state.data.getTypeList = null;
        state.error = null;
      })

      .addCase(
        getVehicleMakeTypeList.fulfilled,
        (state, action: PayloadAction<GetVehicleConfigData>) => {
          state.isLoading.isTypeLoading = false;
          state.data.getTypeList = action.payload;
          state.error = null;
        }
      )

      .addCase(getVehicleMakeTypeList.rejected, (state, action: any) => {
        state.isLoading.isTypeLoading = false;
        state.data.getTypeList = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // Add vehicle config list:
      .addCase(addVehicleConfigList.pending, state => {
        state.isLoading.isAddLoading = true;
        state.data.addVehicleConfig = null;
        state.error = null;
      })

      .addCase(
        addVehicleConfigList.fulfilled,
        (state, action: PayloadAction<AddResponseData>) => {
          state.isLoading.isAddLoading = false;
          state.data.addVehicleConfig = action.payload;
          state.error = null;
        }
      )

      .addCase(addVehicleConfigList.rejected, (state, action: any) => {
        state.isLoading.isAddLoading = false;
        state.data.addVehicleConfig = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // Update vehicle config list:
      .addCase(updateVehicleConfigList.pending, state => {
        state.isLoading.isUpdateLoading = true;
        state.data.updateVehicleConfig = null;
        state.error = null;
      })

      .addCase(
        updateVehicleConfigList.fulfilled,
        (state, action: PayloadAction<AddResponseData>) => {
          state.isLoading.isUpdateLoading = false;
          state.data.updateVehicleConfig = action.payload;
          state.error = null;
        }
      )

      .addCase(updateVehicleConfigList.rejected, (state, action: any) => {
        state.isLoading.isUpdateLoading = false;
        state.data.updateVehicleConfig = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      })

      // Delete vehicle config list:
      .addCase(deleteVehicleConfigList.pending, state => {
        state.isLoading.isDeleteLoading = true;
        state.data.deleteVehicleConfig = null;
        state.error = null;
      })

      .addCase(
        deleteVehicleConfigList.fulfilled,
        (state, action: PayloadAction<DeleteRes>) => {
          state.isLoading.isDeleteLoading = false;
          state.data.deleteVehicleConfig = action.payload;
          state.error = null;
        }
      )

      .addCase(deleteVehicleConfigList.rejected, (state, action: any) => {
        state.isLoading.isDeleteLoading = false;
        state.data.deleteVehicleConfig = null;
        state.error = action?.payload?.data?.error || 'Something went wrong';
      });
  }
});

export default vehicleConfigManagerFile.reducer;
export const {
  clearAllListData,
  clearPartListData,
  clearMakeListData,
  clearTypeListData
} = vehicleConfigManagerFile.actions;
